import { useEffect, useState } from 'react'
import { useFeatureFlagWithPrefs } from '@fs/zion-flags'

export const flagWaitMs = 5000

/**
 * Reads a preference-backed feature flag, giving up on an answer that never arrives.
 *
 * `useFeatureFlagWithPrefs` resolves its `userPreferences` through `usePreference`, which reports a
 * failed single-key read as `undefined` — the same value it reports while the read is in flight — and
 * caches that failure for the session. The two are therefore indistinguishable, `loading` never clears,
 * and a page that holds a skeleton until the flag settles holds it for good.
 *
 * After {@link flagWaitMs} this answers off, and keeps answering off for the rest of the mount even
 * if the real value arrives later. Letting a late answer through would be worse than being wrong:
 * callers memoize on this value to build `initialQueryObj`, and record-search's SearchFormProvider
 * resets the form to its defaults whenever that object's identity changes, so a flip discards what
 * the user has typed and switches the seeded collection param underneath them. Being consistently
 * wrong for one mount costs the new experience; flipping costs their input.
 *
 * The bound is generous rather than tight for the same reason — giving up early on a healthy but
 * slow network is now a decision that stands until the next mount.
 *
 * Stopgap. The fix belongs in zion-preferences, where a failed read should be retried rather than
 * cached as pending; this hook can go away with it.
 *
 * @param {string} flagName - the feature flag to read
 * @param {string[]} [userPreferences] - preference keys the flag depends on
 * @returns {{isOn: boolean, loading: boolean}} the flag, and whether it is still resolving
 */
export default function usePreferenceBackedFlag(flagName, userPreferences = ['fslabs.labs']) {
  const { data, loading } = useFeatureFlagWithPrefs(flagName, null, { userPreferences })
  const [gaveUp, setGaveUp] = useState(false)

  useEffect(() => {
    if (!loading || gaveUp) return undefined
    const timer = setTimeout(() => setGaveUp(true), flagWaitMs)
    return () => clearTimeout(timer)
  }, [loading, gaveUp])

  if (gaveUp) return { isOn: false, loading: false }
  return { isOn: Boolean(data?.isOn), loading }
}
